/**
 * @file     SignUpForm.tsx
 * @purpose  Sign-up form, creates Supabase account, auto-creates FUB contact with source: 'academy', glassmorphic card. An optional header slot replaces the title block.
 */
'use client';

import { useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { SocialAuthButtons } from '@/components/gate/SocialAuthButtons';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import MuiLink from '@mui/material/Link';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { glassBlur } from '@/design-system/tokens';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAppTheme } from '@/core/theme';
import { getSupabaseBrowserClient } from '@/core/supabase/client';
import Link from 'next/link';
import { useEmailConfirmedWatcher } from '@/hooks/useEmailConfirmedWatcher';
import { safeNextPath } from '@/services/auth/emailLinkOrigin';
import { consumePendingIntent } from '@/services/auth/intentPersistence';
import { secureFetch } from '@/services/anti-scraping/secureFetch';
import { FormConsentLine } from '@/components/lead-capture/FormConsentLine';
import { ConsentChoices, useConsentChoices } from '@/components/lead-capture/ConsentChoices';
import { contactPayload, validateContact } from '@/services/leads/contactFields';
import { formatPhoneInput } from '@/utils/formatPhone';

interface SignUpFormProps {
  onSwitchAuthMode?: () => void;
  /** Override the redirect target. Used when SignUpForm is embedded in a modal. */
  redirectOverride?: string;
  /** Called after a successful sign-up instead of router.push. */
  onAuthSuccess?: () => void;
  title?: string;
  subtitle?: string;
  /** Heading level for the form title. Pages that already own an H1 pass 'h2'. */
  headingComponent?: 'h1' | 'h2';
  /** Attribution used when the embedded form creates the FUB lead. */
  leadSourcePage?: string;
  /** Replaces the title and subtitle, e.g. a page intro that owns the H1. */
  header?: React.ReactNode;
}

export const SignUpForm = ({
  onSwitchAuthMode,
  redirectOverride,
  onAuthSuccess,
  title,
  subtitle,
  headingComponent = 'h1',
  leadSourcePage = '/sign-up',
  header,
}: SignUpFormProps = {}) => {
  const { t } = useTranslation();
  const { theme } = useAppTheme();
  const router = useRouter();
  const searchParams = useSearchParams();

  // `redirect` drives the "Already have an account?" link which is server-rendered.
  // Read from searchParams only after mount so SSR and hydration always agree on '/'.
  // Handlers (handleSignUp, handleSendMagicLink) run after user interaction, always
  // client-side, so they can safely read the live value from the ref below.
  const [redirect, setRedirect] = useState('/');
  const redirectRef = useRef('/');
  const audienceRef = useRef('');

  useEffect(() => {
    const safe = safeNextPath(redirectOverride ?? searchParams.get('redirect') ?? searchParams.get('next'), '/');
    redirectRef.current = safe;
    audienceRef.current = searchParams.get('audience') ?? '';
    setRedirect(safe);
  }, [redirectOverride, searchParams]);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const consent = useConsentChoices();
  const name = `${firstName.trim()} ${lastName.trim()}`.trim();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [accountExists, setAccountExists] = useState(false);
  const [shaking, setShaking] = useState(false);

  const formStartTime = useRef(Date.now());

  const dur = theme.motion.durations;
  const ease = theme.motion.easing;
  const transition = `${dur.normal}ms ${ease.standard}`;

  const triggerShake = () => setShaking(true);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const contactErrors = validateContact({ firstName, lastName, email, phone }, t);
    const firstContactError = Object.values(contactErrors)[0];
    if (firstContactError) {
      setError(firstContactError);
      triggerShake();
      return;
    }
    const contact = contactPayload({ firstName, lastName, email, phone });
    setLoading(true);

    const supabase = await getSupabaseBrowserClient();
    if (!supabase) {
      setError('Sign-up is not working right now. Please try again later.');
      setLoading(false);
      triggerShake();
      return;
    }

    // Read live values from refs, handler always runs client-side after mount.
    const liveRedirect = redirectRef.current;
    const liveAudience = audienceRef.current;
    const callbackNext = liveAudience
      ? `${liveRedirect}${liveRedirect.includes('?') ? '&' : '?'}audience=${encodeURIComponent(liveAudience)}`
      : liveRedirect;

    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
          first_name: contact.firstName,
          last_name: contact.lastName,
          phone: contact.phone,
          ...(audienceRef.current ? { pending_audience: audienceRef.current } : {}),
        },
        emailRedirectTo: `${window.location.origin}/auth/finish?next=${encodeURIComponent(callbackNext)}`,
      },
    });

    if (signUpError) {
      setError(signUpError.message);
      setLoading(false);
      triggerShake();
      return;
    }

    // A2: Detect duplicate email (Supabase returns empty identities array).
    // Show a clear prompt to sign in instead of an ambiguous success message.
    if (signUpData.user?.identities?.length === 0) {
      setLoading(false);
      setAccountExists(true);
      return;
    }

    // Only create FUB contact if signUp returned a valid user
    if (signUpData.user) {
      // Mark password_set server-side (app_metadata). If email confirmation is
      // pending (no session), the signIn backfill sets it on first login.
      const { markPasswordSet } = await import('@/services/auth/accountFlagsClient');
      void markPasswordSet('signup-form');
      try {
        const { submitLead } = await import('@/services/leads/submitLead');
        await submitLead({
            type: 'user-signup',
            ...contact,
            ...consent.payload(contact.phone),
            sourcePage: leadSourcePage,
            antiSpam: {
              honeypotFilled: false,
              elapsedSeconds: (Date.now() - formStartTime.current) / 1000,
              submissionTooFast: false,
            },
          },
          { sourcePage: leadSourcePage, flowId: 'userSignup' });
      } catch {
        // Non-blocking
      }
    }

    setLoading(false);

    // If Supabase auto-confirmed (session exists), fire any pending intent
    // (e.g. property inquiry from the MLS detail CTA) then redirect.
    if (signUpData.session) {
      const intent = consumePendingIntent();
      if (intent && intent.kind === 'property_inquiry') {
        try {
          await secureFetch('/api/inquiries', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ listingKey: intent.listingKey }),
          });
        } catch {
          // Non-blocking, intent will not retry automatically; user can re-click
          // the CTA from the listing once signed in.
        }
      }
      if (onAuthSuccess) {
        onAuthSuccess();
      } else {
        router.push(redirectRef.current);
      }
      return;
    }

    // Otherwise show "check your email" message
    setSuccess(true);
  };

  const cardSx = {
    // CSS-var colour: these routes prerender in light, so a raw value would
    // hydrate as a mismatched light card in dark mode.
    backgroundColor: `color-mix(in srgb, ${theme.semanticColors.surfaceCard} 60%, transparent)`,
    border: `1px solid ${theme.semanticColors.borderSubtle}`,
    borderRadius: `${theme.layout.radius.lg}px`,
    backdropFilter: glassBlur.strong,
    p: { xs: 5, sm: 6 },
    width: '100%',
    maxWidth: 420,
    mx: 'auto',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: `${theme.layout.form.fieldGap}px`,
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': { borderRadius: `${theme.layout.radius.md}px` },
  };

  const btnSx = {
    textTransform: 'none' as const,
    borderRadius: `${theme.layout.radius.md}px`,
    fontWeight: 500,
    py: 1.2,
    transition: `background-color ${transition}, transform ${dur.fast}ms ${ease.standard}`,
    '&:active': { transform: 'scale(0.98)' },
  };

  const linkSx = {
    color: theme.semanticColors.textMuted,
    cursor: 'pointer',
    fontSize: 13,
    textDecoration: 'none',
    transition: `color ${transition}`,
    '&:hover': { color: theme.semanticColors.interactivePrimary },
  };


  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [sendingMagicLink, setSendingMagicLink] = useState(false);

  // Route through the server resend endpoint rather than signInWithOtp: that
  // sends Supabase's own template, whose one-time /auth/v1/verify link inbox
  // scanners consume before the user clicks. The endpoint sends a Relta HMAC
  // link redeemed by POST at click time instead.
  //
  // For an email that already has an account, `next` is /set-password: many of
  // those accounts were minted passwordless by the listings gate or the lead
  // pipeline, so "sign in with your password" is advice they cannot follow. On
  // the "check your email" screen the person just chose a password in this
  // form, so the link goes to where they were headed instead.
  const handleSendMagicLink = async (setPassword: boolean) => {
    setSendingMagicLink(true);
    setError(null);
    try {
      const res = await secureFetch('/api/auth/resend-magic-link', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          email,
          next: setPassword ? `/set-password?next=${encodeURIComponent(redirectRef.current)}` : redirectRef.current,
        }),
      });
      if (res.status === 429) {
        setError(t('auth.signUp.resendThrottled', { defaultValue: 'Try again in a minute.' }));
      } else if (!res.ok) {
        setError(t('auth.signUp.resendFailed', { defaultValue: 'Could not send the link. Try again in a moment.' }));
      } else {
        setMagicLinkSent(true);
      }
    } catch {
      setError(t('auth.signUp.resendFailed', { defaultValue: 'Could not send the link. Try again in a moment.' }));
    }
    setSendingMagicLink(false);
  };

  // The link is usually opened in another tab or app; carry on from here once it is.
  useEmailConfirmedWatcher(success, () => {
    if (onAuthSuccess) onAuthSuccess();
    else router.push(redirectRef.current);
  });

  if (accountExists) {
    return (
      <Box sx={cardSx}>
        <Box
          sx={{
            backgroundColor: alpha(theme.rawSemanticColors.statusWarning, 0.08),
            border: `1px solid ${theme.semanticColors.borderSubtle}`,
            borderRadius: `${theme.layout.radius.md}px`,
            px: 2,
            py: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            {t('auth.signUp.accountExistsTitle', { defaultValue: 'You already have an account' })}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.semanticColors.textSecondary, mb: 3 }}>
            {t('auth.signUp.accountExistsBody', {
              defaultValue: '{{email}} already has an account. Sign in, or get a link to set a password.',
              email,
            })}
          </Typography>
          {error && (
            <Typography variant="body2" sx={{ color: theme.semanticColors.statusError, fontSize: 13, mb: 2 }}>
              {error}
            </Typography>
          )}
          <Stack spacing={2}>
            {!magicLinkSent ? (
              <Button
                variant="contained"
                fullWidth
                onClick={() => handleSendMagicLink(true)}
                disabled={sendingMagicLink}
                sx={btnSx}
              >
                {sendingMagicLink
                  ? t('auth.signUp.sendingMagicLink', { defaultValue: 'Sending...' })
                  : t('auth.signUp.accountExistsMagicLink', { defaultValue: 'Send a sign-in link' })}
              </Button>
            ) : (
              <Typography variant="body2" sx={{ color: theme.semanticColors.textSecondary }}>
                {t('auth.signUp.magicLinkSent', { defaultValue: 'Sign-in link sent. Open it to set a password.' })}
              </Typography>
            )}
            {onSwitchAuthMode && (
              <Button variant="outlined" fullWidth onClick={onSwitchAuthMode} sx={{ ...btnSx, fontWeight: 500 }}>
                {t('auth.signUp.accountExistsSignIn', { defaultValue: 'Sign in with password' })}
              </Button>
            )}
          </Stack>
        </Box>
      </Box>
    );
  }

  if (success) {
    return (
      <Box sx={cardSx}>
        <Box
          sx={{
            backgroundColor: alpha(theme.rawSemanticColors.statusSuccess, 0.08),
            border: `1px solid ${alpha(theme.rawSemanticColors.statusSuccess, 0.2)}`,
            borderRadius: `${theme.layout.radius.md}px`,
            px: 2,
            py: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            {t('auth.signUp.successTitle', { defaultValue: 'Check your email' })}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.semanticColors.textSecondary, mb: 2 }}>
            {t('auth.signUp.successMessage', {
              defaultValue: "If an account can be created for {{email}}, a confirmation link is on its way.",
              email,
            })}
          </Typography>
          {error && (
            <Typography variant="body2" sx={{ color: theme.semanticColors.statusError, fontSize: 13, mb: 2 }}>
              {error}
            </Typography>
          )}

          {!magicLinkSent ? (
            <Button
              variant="outlined"
              fullWidth
              onClick={() => handleSendMagicLink(false)}
              disabled={sendingMagicLink}
              sx={{ ...btnSx, fontWeight: 500 }}
            >
              {sendingMagicLink
                ? t('auth.signUp.sendingMagicLink', { defaultValue: 'Sending...' })
                : t('auth.signUp.magicLinkFallback', { defaultValue: "Didn't get it? Email me a sign-in link" })}
            </Button>
          ) : (
            <Typography variant="body2" sx={{ color: theme.semanticColors.statusSuccess, fontWeight: 500 }}>
              {t('auth.signUp.magicLinkSent', { defaultValue: 'Sign-in link sent. Check your inbox.' })}
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSignUp}
        onAnimationEnd={() => setShaking(false)}
        className={shaking ? 'relta-form-shake' : undefined}
        sx={cardSx}
      >
        {header ?? (
          <>
            <Typography variant="h4" component={headingComponent} sx={{ fontWeight: 600, textAlign: 'center' }}>
              {title ?? t('auth.signUp.title', { defaultValue: 'Create your account' })}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.semanticColors.textSecondary, textAlign: 'center', mb: 0.5 }}>
              {subtitle ?? t('auth.signUp.subtitle', { defaultValue: 'Save homes, get alerts, and see sold prices with a free account.' })}
            </Typography>
          </>
        )}

        {error && (
          <Box
            sx={{
              backgroundColor: alpha(theme.rawSemanticColors.statusError, 0.08),
              border: `1px solid ${alpha(theme.rawSemanticColors.statusError, 0.2)}`,
              borderRadius: `${theme.layout.radius.md}px`,
              px: 2,
              py: 1.2,
            }}
          >
            <Typography variant="body2" sx={{ color: theme.semanticColors.statusError, fontSize: 13 }}>
              {error}
            </Typography>
          </Box>
        )}

        <SocialAuthButtons next={redirectOverride ?? (onAuthSuccess ? undefined : redirect)} disabled={loading} />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label={t('auth.signUp.firstNameLabel', { defaultValue: 'First name' })}
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            fullWidth
            autoComplete="given-name"
            autoFocus
            sx={inputSx}
          />
          <TextField
            label={t('auth.signUp.lastNameLabel', { defaultValue: 'Last name' })}
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            fullWidth
            autoComplete="family-name"
            sx={inputSx}
          />
        </Stack>
        <TextField
          label={t('auth.signUp.emailLabel', { defaultValue: 'Email' })}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          fullWidth
          autoComplete="email"
          sx={inputSx}
        />
        <Box>
          <TextField
            label={t('forms.contact.phoneLabel', { defaultValue: 'Mobile phone' })}
            type="tel"
            value={phone}
            onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
            required
            fullWidth
            autoComplete="tel"
            placeholder={t('forms.contact.phonePlaceholder', { defaultValue: '(604) 555-0123' })}
            inputProps={{ inputMode: 'tel' }}
            sx={inputSx}
          />
        </Box>
        <TextField
          label={t('auth.signUp.passwordLabel', { defaultValue: 'Password' })}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          fullWidth
          autoComplete="new-password"
          inputProps={{ minLength: 8 }}
          helperText={t('auth.signUp.passwordHint', { defaultValue: 'At least 8 characters' })}
          sx={inputSx}
        />

        <ConsentChoices phone values={consent.values} onChange={consent.setValues} disabled={loading} />

        <Button type="submit" variant="contained" fullWidth disabled={loading} sx={btnSx}>
          {loading
            ? t('auth.signUp.creating', { defaultValue: 'Creating account...' })
            : t('auth.signUp.action', { defaultValue: 'Create account' })}
        </Button>

        <FormConsentLine />

        <Box sx={{ textAlign: 'center' }}>
          {onSwitchAuthMode ? (
            <MuiLink
              component="button"
              type="button"
              onClick={onSwitchAuthMode}
              variant="body2"
              sx={linkSx}
            >
              {t('auth.signUp.haveAccount', { defaultValue: 'Already have an account? Sign in' })}
            </MuiLink>
          ) : (
            <MuiLink component={Link} href={`/sign-in?redirect=${encodeURIComponent(redirect)}`} variant="body2" sx={linkSx}>
              {t('auth.signUp.haveAccount', { defaultValue: 'Already have an account? Sign in' })}
            </MuiLink>
          )}
        </Box>
      </Box>
    </>
  );
};
